/**
 * iter211 — Sessions calendar grid view.
 * Renders a month-grid where each day cell shows the sessions on that date as
 * coloured dots / status pills. Click a session to focus it (same effect as
 * clicking the row in list view).
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function buildStart(s) {
  if (!s?.date) return new Date(0);
  const [yy, mo, dd] = s.date.split('-').map((x) => parseInt(x, 10) || 0);
  const [hh, mm] = (s.start_time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  return new Date(yy, mo - 1, dd, hh, mm);
}
function statusColor(s) {
  if (s.status === 'cancelled') return 'bg-red-400 text-red-50 border-red-500';
  if (s.status === 'completed') return 'bg-emerald-500 text-emerald-50 border-emerald-600';
  if (s.status === 'live' || s.is_live) return 'bg-orange-500 text-orange-50 border-orange-600 animate-pulse';
  if (s.status === 'open' || s.status === 'rsvp_open') return 'bg-amber-400 text-amber-50 border-amber-500';
  if (s.status === 'rsvp_closed') return 'bg-blue-500 text-blue-50 border-blue-600';
  return 'bg-slate-400 text-slate-50 border-slate-500';
}

export default function SessionsCalendar({ sessions, onSelectSession }) {
  // Anchor month — defaults to "the month containing the latest session" so
  // admin lands somewhere useful instead of a blank Feb 2026.
  const initialAnchor = useMemo(() => {
    if (!sessions || sessions.length === 0) return new Date();
    const dates = sessions
      .map((s) => buildStart(s).getTime())
      .filter((t) => Number.isFinite(t) && t > 0);
    if (dates.length === 0) return new Date();
    // Pick the most-recent date that's not absurdly in the future
    const sorted = dates.sort((a, b) => b - a);
    return new Date(sorted[0]);
  }, [sessions]);

  const [anchor, setAnchor] = useState(initialAnchor);

  const monthGrid = useMemo(() => {
    const first = startOfMonth(anchor);
    const last = endOfMonth(anchor);
    const startDay = first.getDay(); // 0 = Sun
    const totalDays = last.getDate();
    // Build a 6-week (42-cell) grid for stable layout
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push({ day: null, sessions: [] });
    // Bucket sessions by day-of-month
    const byDay = {};
    sessions.forEach((s) => {
      const sd = buildStart(s);
      if (sd.getFullYear() === anchor.getFullYear() && sd.getMonth() === anchor.getMonth()) {
        const dd = sd.getDate();
        (byDay[dd] = byDay[dd] || []).push(s);
      }
    });
    for (let d = 1; d <= totalDays; d++) {
      cells.push({ day: d, sessions: byDay[d] || [] });
    }
    while (cells.length < 42) cells.push({ day: null, sessions: [] });
    return cells;
  }, [anchor, sessions]);

  const monthLabel = anchor.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  const today = new Date();
  const isThisMonth = today.getFullYear() === anchor.getFullYear() && today.getMonth() === anchor.getMonth();

  const prevMonth = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const nextMonth = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  const jumpToday = () => setAnchor(new Date());

  return (
    <div className="border-2 rounded-2xl p-3 space-y-3 bg-card" data-testid="sessions-calendar">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={prevMonth} data-testid="cal-prev-month" className="h-8 px-2">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex flex-col items-center">
          <span className="font-display font-bold text-sm">{monthLabel}</span>
          {!isThisMonth && (
            <button
              type="button"
              onClick={jumpToday}
              className="text-xs text-primary underline underline-offset-2"
              data-testid="cal-jump-today"
            >
              Jump to today
            </button>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={nextMonth} data-testid="cal-next-month" className="h-8 px-2">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="text-xs uppercase font-bold text-muted-foreground">
            {d}
          </span>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-1">
        {monthGrid.map((cell, idx) => {
          const isToday =
            cell.day &&
            isThisMonth &&
            cell.day === today.getDate();
          return (
            <div
              key={idx}
              className={`min-h-[56px] rounded-md border p-1 flex flex-col gap-0.5 ${
                cell.day ? 'bg-background' : 'bg-muted/20 opacity-40'
              } ${isToday ? 'ring-2 ring-primary' : ''}`}
              data-testid={cell.day ? `cal-day-${anchor.getFullYear()}-${anchor.getMonth() + 1}-${cell.day}` : undefined}
            >
              {cell.day && (
                <span className={`text-xs font-semibold ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                  {cell.day}
                </span>
              )}
              <div className="flex flex-col gap-0.5">
                {cell.sessions.slice(0, 2).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onSelectSession?.(s)}
                    title={`${s.title} · ${s.start_time || ''} · ${s.status}`}
                    className={`text-xs truncate rounded px-1 py-0.5 font-semibold border ${statusColor(s)}`}
                    data-testid={`cal-session-${s.id}`}
                  >
                    {(s.start_time || '').slice(0, 5)}{s.title ? ' ' + s.title.slice(0, 10) : ''}
                  </button>
                ))}
                {cell.sessions.length > 2 && (
                  <span className="text-xs text-muted-foreground font-semibold">
                    +{cell.sessions.length - 2}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground pt-2 border-t">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-amber-400" /> RSVP open
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-blue-500" /> RSVP closed
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-orange-500" /> Live
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-emerald-500" /> Completed
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-red-400" /> Cancelled
        </span>
      </div>
    </div>
  );
}
